import React, { useState } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { cn } from '@frontend/shadcn/lib/utils';
import {
  FileIcon,
  XmarkIcon,
  CheckIcon,
  EnvelopeIcon,
  CircleDollarIcon,
  PenPaintbrushIcon
} from '@revfluence/fresh-icons/regular/esm';
import { NewspaperIcon } from '@revfluence/fresh-icons/solid/esm';
import { useStageTemplates } from '@frontend/app/hooks/useStageTemplates';
import type { GetStageTemplates_getStageTemplates as IStageTemplate, GetStageTemplates_getStageTemplates_statuses as ITemplateStatus } from '@frontend/app/queries/types/GetStageTemplates';

// Template visual configurations
const templateConfigs: Record<string, { icon: React.ReactNode; iconBgColor: string }> = {
  'Ad-Ready Content': {
    icon: <FileIcon className="text-white text-sm" />,
    iconBgColor: 'bg-pink-600'
  },
  'Affiliate Newsletter': {
    icon: <NewspaperIcon className="text-white text-sm" />,
    iconBgColor: 'bg-green-600'
  },
  'Concept Review': {
    icon: <FileIcon className="text-white text-sm" />,
    iconBgColor: 'bg-purple-600'
  },
  'Content Rates': {
    icon: <CircleDollarIcon className="text-white text-sm" />,
    iconBgColor: 'bg-yellow-500'
  },
  'Welcome Email': {
    icon: <EnvelopeIcon className="text-white text-sm" />,
    iconBgColor: 'bg-blue-500'
  },
  'Blank Custom Stage': {
    icon: <PenPaintbrushIcon className="text-white text-sm" />,
    iconBgColor: 'bg-teal-500'
  }
};

const StatusBadge: React.FC<{ status: ITemplateStatus }> = ({ status }) => {
  return (
    <div
      className={cn('px-2 py-0.5 rounded-lg flex justify-center items-center gap-1 text-xs font-normal text-gray-800', status.bgColor)}
    >
      {status.name}
    </div>
  );
};

interface TemplateCardProps {
  template: IStageTemplate | { id: 'blank'; name: 'Blank Custom Stage'; description: string; statuses: ITemplateStatus[] };
  isSelected: boolean;
  onSelect: (template: IStageTemplate | { id: 'blank'; name: 'Blank Custom Stage'; description: string; statuses: ITemplateStatus[] }) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, isSelected, onSelect }) => {
  const config = templateConfigs[template.name] || {
    icon: <FileIcon className="text-white text-sm" />,
    iconBgColor: 'bg-gray-500'
  };

  // Show only first 2 statuses and +N if there are more
  const displayStatuses = template.statuses.slice(0, 2);
  const remainingCount = template.statuses.length - 2;

  return (
    <div
      className={cn(
        'flex-1 px-4 py-3 bg-white rounded-2xl outline outline-1 outline-offset-[-1px]',
        'flex flex-col justify-start items-start gap-2 relative cursor-pointer',
        isSelected ? 'outline-gray-800' : 'outline-gray-200'
      )}
      onClick={() => onSelect(template)}
    >
      <div className="self-stretch flex flex-col justify-start items-start">
        <div className="self-stretch flex justify-start items-center gap-2">
          <div className={cn('w-6 h-6 p-2 rounded-lg flex justify-center items-center', config.iconBgColor)}>
            {config.icon}
          </div>
          <div className="flex-1 text-gray-800 text-sm font-medium leading-6">
            {template.name}
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-center items-start gap-1">
          <div className="self-stretch text-gray-500 text-xs font-normal leading-5">
            {template.description}
          </div>
        </div>
      </div>
      <div className="self-stretch flex justify-start items-start gap-1 flex-wrap content-start">
        {displayStatuses.map((status, index) => (
          <StatusBadge key={index} status={status} />
        ))}
        {remainingCount > 0 && (
          <div className="px-2 py-0.5 bg-gray-200 rounded-lg flex justify-center items-center gap-1 text-xs font-normal text-gray-800">
            +{remainingCount}
          </div>
        )}
      </div>
      {isSelected && (
        <div className="h-4 absolute -top-1 -right-1 bg-white rounded-lg outline outline-2 outline-white flex justify-center items-center">
          <CheckIcon className="text-gray-800 text-base" />
        </div>
      )}
    </div>
  );
};

interface StageTemplateSelectorProps {
  onSelectTemplate: (template: IStageTemplate | null) => void;
  onCancel: () => void;
}

export const StageTemplateSelector: React.FC<StageTemplateSelectorProps> = ({
  onSelectTemplate,
  onCancel
}) => {
  const { templates, loading } = useStageTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<IStageTemplate | { id: 'blank'; name: 'Blank Custom Stage'; description: string; statuses: ITemplateStatus[] } | null>(null);

  const blankTemplate = {
    id: 'blank' as const,
    name: 'Blank Custom Stage' as const,
    description: 'Create your own custom stage',
    statuses: [{ name: 'New Status', bgColor: 'bg-gray-200' }] as ITemplateStatus[]
  };

  const allTemplates = [...templates, blankTemplate];

  const handleSelect = (template: IStageTemplate | { id: 'blank'; name: 'Blank Custom Stage'; description: string; statuses: ITemplateStatus[] }) => {
    setSelectedTemplate(template);
  };

  const handleAddStage = () => {
    if (selectedTemplate) {
      if (selectedTemplate.id === 'blank') {
        onSelectTemplate(null); // This will trigger the blank custom stage flow
      } else {
        onSelectTemplate(selectedTemplate as IStageTemplate);
      }
    }
  };

  if (loading) {
    return (
      <div className="self-stretch p-4 bg-gray-50 rounded-2xl outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col justify-start items-start gap-2">
        <div className="text-gray-500">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="self-stretch p-4 bg-gray-50 rounded-2xl outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col justify-start items-start gap-2">
      <div className="self-stretch flex justify-start items-center gap-2">
        <div className="flex-1 text-gray-800 text-sm font-medium leading-6">
          Select a stage template or create a blank custom stage.
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="w-8 h-8 p-2 rounded-lg justify-center items-center"
        >
          <XmarkIcon className="text-gray-800 text-sm" />
        </Button>
      </div>

      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        {/* First row - 2 templates */}
        <div className="self-stretch flex justify-start items-start gap-2">
          {allTemplates.slice(0, 2).map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={selectedTemplate?.id === template.id}
              onSelect={handleSelect}
            />
          ))}
        </div>

        {/* Second row - 2 templates */}
        <div className="self-stretch flex justify-start items-start gap-2">
          {allTemplates.slice(2, 4).map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={selectedTemplate?.id === template.id}
              onSelect={handleSelect}
            />
          ))}
        </div>

        {/* Third row - 2 templates */}
        <div className="self-stretch flex justify-start items-start gap-2">
          {allTemplates.slice(4, 6).map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={selectedTemplate?.id === template.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-start items-center gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={handleAddStage}
          disabled={!selectedTemplate}
          className="px-2 py-1 bg-gray-800 text-white rounded-lg text-xs font-medium"
        >
          Add Stage
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="px-2 py-1 bg-white border border-gray-200 text-gray-800 rounded-lg text-xs font-medium"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
